import { useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { Navigate, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setupUserSession } from "../auth/postLoginSetup";
import { clearStaleAuthTokens } from "../auth/oktaTokens";

interface PostLoginRedirectProps {
  settings: {
    server?: {
      baseUrl?: string;
    };
  };
}

function PostLoginRedirect({ settings }: PostLoginRedirectProps) {
  const { authState } = useOktaAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      return;
    }

    clearStaleAuthTokens();

    const baseUrl = settings?.server?.baseUrl || import.meta.env.VITE_BASE_URL;
    if (!baseUrl) {
      setError("Application base URL is not configured.");
      return;
    }

    let cancelled = false;

    setupUserSession(baseUrl)
      .then((path) => {
        if (!cancelled && path) {
          setRedirectPath(path);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err?.message || "Failed to load user profile.";
          const isUnauthorized = /401|unauthorized/i.test(message);
          const isServerError = /500|internal server error/i.test(message);
          setError(
            isUnauthorized
              ? "Signed in with Okta, but the API rejected the token. Verify Okta issuer URLs and that the backend OKTA_API_TOKEN is configured."
              : isServerError
                ? "Signed in with Okta, but the backend could not load your profile. Set OKTA_API_TOKEN on the API server (Okta Admin → Security → API → Create Token), then restart the backend."
                : message
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authState, settings]);

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [redirectPath, navigate]);

  if (authState === null) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="text-center" style={{ padding: "2rem" }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-triangle" />
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="text-center" style={{ padding: "2rem" }}>
          <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem" }} />
          <p className="subtitle" style={{ marginTop: "1rem" }}>
            Loading your workspace…
          </p>
        </div>
      </div>
    </div>
  );
}

export default connect((state: { settings: PostLoginRedirectProps["settings"] }) => ({
  settings: state.settings,
}))(PostLoginRedirect);
